import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import GenericSmallIconButton from 'flow/components/buttons/GenericSmallIconButton';
import {
    isRegisteredSIP,
    NewCallBlockReason,
    ExtraHeaders,
} from 'flow/utils/call-utils';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import { useOutgoingCallsEnabled } from 'flow/hooks/api/useSoftphoneCallSettings';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';

import { TypedMap } from 'flow/types/TypedMap';
import { Extension } from 'flow/types/Extension';
import { Conference } from 'flow/types/Pbx';
import {
    getIsPhoneConnected,
    hasActiveCallsBlockingNewCall,
} from 'flow/selectors/call-selectors';
import { requestPin } from 'flow/actions/meeting-actions';
import { OriginalPlacements } from './ui-components/Tooltip';

interface Props {
    extensionToCall?: TypedMap<Extension>;
    phoneNumber: string;
    tooltipPlacement?: OriginalPlacements;
    size?: 'small' | 'medium' | 'large';
    activeBg?: boolean;
    rounded?: boolean;
    inactiveBgColor?: string;
    conferenceDTO?: TypedMap<Conference>;
    className?: string;
    id?: string;
}

const CallButton = ({
    extensionToCall,
    phoneNumber,
    tooltipPlacement,
    size = 'small',
    activeBg = false,
    rounded,
    inactiveBgColor,
    conferenceDTO,
    className,
    id,
}: Props) => {
    const { i18next, embedded } = useAppContext();
    const { ownExtension } = useExtension();
    const dispatch = useAppDispatch();

    const hasCallBlockingNewCall = useAppSelector(
        (state) =>
            hasActiveCallsBlockingNewCall(state, 'outgoing') !==
            NewCallBlockReason.NONE,
    );

    const hasConnectedPhone = useAppSelector(getIsPhoneConnected);
    const outgoingCallsEnabled = useOutgoingCallsEnabled();

    const handleClick = async (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        let extraCallOptions = {};
        if (conferenceDTO) {
            const meetingPin = conferenceDTO.get('pin');
            const meetingCode = conferenceDTO.get('meetingCode');
            const formattedPin = meetingPin
                ? meetingPin.replace(/ /g, '')
                : await dispatch(requestPin(meetingCode));
            extraCallOptions = {
                extraHeaders: [
                    `${ExtraHeaders.CONFERENCE_PIN.value}: ${formattedPin}`,
                ],
            };
        }

        dispatch(
            showCallDialog(phoneNumber, extensionToCall, extraCallOptions),
        );
    };

    if (
        isTeamsTypeIntegration(embedded) ||
        (!hasConnectedPhone && !isRegisteredSIP(ownExtension)) ||
        hasCallBlockingNewCall
    ) {
        return null;
    }

    return (
        <GenericSmallIconButton
            id={id}
            label={i18next.t(
                outgoingCallsEnabled
                    ? 'Tooltip.Title.Call'
                    : 'settings.softphone.outgoing_calls_disabled',
            )}
            tooltipPlacement={tooltipPlacement}
            iconKey={'call'}
            onClick={outgoingCallsEnabled ? handleClick : undefined}
            size={size}
            activeBg={outgoingCallsEnabled ? activeBg : false}
            inactiveBgColor={inactiveBgColor}
            className={twMerge(rounded && 'rounded-xl', className)}
            disabled={!outgoingCallsEnabled}
        />
    );
};

export default CallButton;
